/* eslint-disable no-underscore-dangle */
// Track resource loading state globally to prevent duplicate loads
window.__insidedEmbed = window.__insidedEmbed || {
  loaded: false,
  loading: null,
};

// Load a single script and return a promise
function loadScript(script, target = document.body) {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script');

    Array.from(script.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });

    if (script.src) {
      newScript.onload = resolve;
      newScript.onerror = reject;
    } else {
      newScript.textContent = script.textContent;
    }

    target.appendChild(newScript);

    if (!script.src) {
      resolve();
    }
  });
}

// Load widget resources into <head> (only once)
async function loadWidgetResources(communityUrl) {
  const response = await fetch(`${communityUrl}/widget/resources?css=false`, {
    method: 'GET',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) {
    return;
  }

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = Array.from(doc.head.children);

  // eslint-disable-next-line no-await-in-loop
  for (const el of elements) {
    if (el.tagName === 'SCRIPT') {
      // eslint-disable-next-line no-await-in-loop
      await loadScript(el, document.head);
    } else {
      document.head.appendChild(el.cloneNode(true));
    }
  }
}

// Load widget resourcesBody scripts (only once)
async function loadWidgetResourcesBody(communityUrl) {
  const response = await fetch(`${communityUrl}/widget/resourcesBody`, {
    method: 'GET',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) {
    return;
  }

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const scripts = Array.from(doc.querySelectorAll('script'));

  // eslint-disable-next-line no-await-in-loop
  for (const script of scripts) {
    // eslint-disable-next-line no-await-in-loop
    await loadScript(script);
  }
}

// Load all resources (called once by first element)
async function loadAllResources(communityUrl) {
  await loadWidgetResources(communityUrl);
  await loadWidgetResourcesBody(communityUrl);
  window.__insidedEmbed.loaded = true;
}

// Ensure resources are loaded (deduped)
function ensureResourcesLoaded(communityUrl) {
  if (window.__insidedEmbed.loaded) {
    return Promise.resolve();
  }
  if (!window.__insidedEmbed.loading) {
    window.__insidedEmbed.loading = loadAllResources(communityUrl);
  }
  return window.__insidedEmbed.loading;
}

// Initialize Shadow DOM for older browsers
function initializeShadowDOM(container) {
  const widget = container.querySelector('insided-widget');
  if (!widget || widget.shadowRoot) {
    return;
  }

  const template = widget.querySelector('template[shadowrootmode]');
  if (!template) {
    return;
  }

  const shadowRoot = widget.attachShadow({ mode: 'open' });
  shadowRoot.innerHTML = template.innerHTML;
  template.remove();
}

// Custom element for embedding widgets
class CCEmbed extends HTMLElement {
  static get observedAttributes() {
    return ['page', 'type', 'community-url'];
  }

  async connectedCallback() {
    await this.loadWidget();
  }

  async attributeChangedCallback() {
    if (this.isConnected) {
      await this.loadWidget();
    }
  }

  async loadWidget() {
    const page = this.getAttribute('page');
    const type = this.getAttribute('type');
    const communityUrl = this.getAttribute('community-url');

    if (!page || !type || !communityUrl) {
      return;
    }

    // Ensure resources are loaded first
    await ensureResourcesLoaded(communityUrl);

    // Load widget HTML
    const params = new URLSearchParams({ page, type });
    const url = `${communityUrl}/widget/embed?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      return;
    }

    const html = await response.text();
    this.innerHTML = html;

    initializeShadowDOM(this);

    // Bind components if insided is ready (since insidedReady event already fired)
    const widget = this.querySelector('insided-widget');
    if (widget?.shadowRoot && window.insided?.bindPreactComponents) {
      window.insided.bindPreactComponents(widget.shadowRoot);
      widget.dispatchEvent(
        new CustomEvent('widget-ready', {
          detail: { widgetType: widget.getAttribute('data-widget-type') },
        })
      );
    }
  }
}

if (!customElements.get('insided-embed')) {
  customElements.define('insided-embed', CCEmbed);
}
/* eslint-enable no-underscore-dangle */
